import styled, {keyframes} from "styled-components";
import {motion} from "framer-motion";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import SmartImage from "../../lib/UIBookBrd/SmartImage";
import {container, displayHeading, down, eyebrow, focusRing, glass, hideScrollbar} from "../../Styles/media";

/* ------------------------------------------------------------------ page head */

const Page = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    padding-bottom: clamp(56px, 8vw, 110px);
`

const Head = styled.div`
    ${container};
    display: flex;
    align-items: flex-end;
    gap: 32px;
    padding-top: clamp(38px, 6vw, 76px);
    padding-bottom: clamp(24px, 3vw, 40px);

    ${down('md')} {
        flex-direction: column;
        align-items: flex-start;
        gap: 14px;
        padding-top: 30px;
        padding-bottom: 20px;
    }
`

const HeadText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
`

const Eyebrow = styled.span`
    ${eyebrow};
    display: inline-flex;
    align-items: center;
    gap: 10px;

    &::before {
        content: '';
        width: 22px;
        height: 1px;
        background-color: ${({theme}) => theme.accent};
    }
`

const Title = styled.h1`
    ${displayHeading};
    font-size: clamp(32px, 5.4vw, 68px);
`

const Lead = styled.p`
    max-width: 58ch;
    font-size: clamp(15px, 1.4vw, 17px);
    line-height: 1.65;
    color: ${({theme}) => theme.colorMuted};
`

const HeadCount = styled.div`
    margin-left: auto;
    padding: 10px 18px;
    border: 1px solid ${({theme}) => theme.line};
    border-radius: ${({theme}) => theme.radius.pill};
    font-size: 13px;
    font-weight: 600;
    color: ${({theme}) => theme.colorMuted};
    white-space: nowrap;

    ${down('md')} {
        margin-left: 0;
    }
`

/* ------------------------------------------------------------------- toolbar */

const Toolbar = styled.div`
    position: sticky;
    top: ${({theme}) => theme.header.height}px;
    z-index: 500;
    ${glass};
    border-top: 1px solid ${({theme}) => theme.lineFaint};
    border-bottom: 1px solid ${({theme}) => theme.line};

    ${down('md')} {
        top: ${({theme}) => theme.header.heightMobile}px;
    }
`

const ToolbarInner = styled.div`
    ${container};
    display: flex;
    align-items: center;
    gap: 14px;
    padding-top: 12px;
    padding-bottom: 12px;

    ${down('lg')} {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
    }
`

const Tabs = styled.div`
    ${hideScrollbar};
    display: flex;
    gap: 6px;
    overflow-x: auto;
    padding: 3px;
    border: 1px solid ${({theme}) => theme.line};
    border-radius: ${({theme}) => theme.radius.pill};
`

const Tab = styled.button<{ $active: boolean }>`
    ${focusRing};
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    height: 36px;
    padding: 0 16px;
    border-radius: ${({theme}) => theme.radius.pill};
    font-size: 13.5px;
    font-weight: 600;
    white-space: nowrap;
    color: ${({theme, $active}) => ($active ? theme.accentContrast : theme.colorMuted)};
    background-color: ${({theme, $active}) => ($active ? theme.color : 'transparent')};
    transition: color 220ms ${({theme}) => theme.ease.out}, background-color 220ms ${({theme}) => theme.ease.out};

    &:hover {
        color: ${({theme, $active}) => ($active ? theme.accentContrast : theme.color)};
        background-color: ${({theme, $active}) => ($active ? theme.color : 'rgba(255, 255, 255, 0.06)')};
    }
`

const TabCount = styled.span<{ $active: boolean }>`
    font-size: 11px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    opacity: ${({$active}) => ($active ? 0.6 : 0.8)};
    color: ${({theme, $active}) => ($active ? theme.accentContrast : theme.colorFaint)};
`

const ToolbarRight = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-left: auto;

    ${down('lg')} {
        margin-left: 0;
    }

    ${down('sm')} {
        flex-direction: column;
        align-items: stretch;
    }
`

const Search = styled.label`
    display: flex;
    align-items: center;
    gap: 10px;
    width: clamp(200px, 26vw, 300px);
    height: 42px;
    padding: 0 14px;
    border: 1px solid ${({theme}) => theme.line};
    border-radius: ${({theme}) => theme.radius.sm};
    background-color: rgba(255, 255, 255, 0.035);
    transition: border-color 220ms ${({theme}) => theme.ease.out};

    &:focus-within {
        border-color: ${({theme}) => theme.accentLine};
    }

    ${down('sm')} {
        width: 100%;
    }
`

const SearchIcon = styled(FontAwesomeIcon)`
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    color: ${({theme}) => theme.colorFaint};
`

const SearchInput = styled.input`
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-size: 14px;
    color: ${({theme}) => theme.color};

    &::placeholder {
        color: ${({theme}) => theme.colorFaint};
    }
`

const ClearSearch = styled.button`
    ${focusRing};
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    color: ${({theme}) => theme.colorFaint};

    &:hover {
        color: ${({theme}) => theme.color};
        background-color: rgba(255, 255, 255, 0.1);
    }
`

const SortHolder = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`

const Sort = styled.select`
    ${focusRing};
    appearance: none;
    height: 42px;
    padding: 0 38px 0 40px;
    border: 1px solid ${({theme}) => theme.line};
    border-radius: ${({theme}) => theme.radius.sm};
    background-color: rgba(255, 255, 255, 0.035);
    font-size: 14px;
    font-weight: 600;
    color: ${({theme}) => theme.color};
    cursor: pointer;
    transition: border-color 220ms ${({theme}) => theme.ease.out};

    &:hover {
        border-color: ${({theme}) => theme.lineStrong};
    }

    option {
        background-color: ${({theme}) => theme.surfaceRaised};
        color: ${({theme}) => theme.color};
    }

    ${down('sm')} {
        width: 100%;
    }
`

const SortIcon = styled(FontAwesomeIcon)`
    position: absolute;
    left: 15px;
    width: 14px;
    height: 14px;
    pointer-events: none;
    color: ${({theme}) => theme.colorFaint};
`

const SortChevron = styled(FontAwesomeIcon)`
    position: absolute;
    right: 15px;
    width: 11px;
    height: 11px;
    pointer-events: none;
    color: ${({theme}) => theme.colorFaint};
`

/* ---------------------------------------------------------------------- grid */

const Grid = styled.div`
    ${container};
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(272px, 100%), 1fr));
    gap: clamp(12px, 1.5vw, 20px);
    padding-top: clamp(22px, 3vw, 36px);

    ${down('xs')} {
        grid-template-columns: minmax(0, 1fr);
    }
`

/* ---------------------------------------------------------------------- card */

const Card = styled(motion.article)`
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid ${({theme}) => theme.line};
    border-radius: ${({theme}) => theme.radius.md};
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.012));
    transition: border-color 300ms ${({theme}) => theme.ease.out},
    transform 300ms ${({theme}) => theme.ease.out},
    box-shadow 300ms ${({theme}) => theme.ease.out};

    &:hover {
        border-color: ${({theme}) => theme.lineStrong};
        transform: translateY(-4px);
        box-shadow: ${({theme}) => theme.shadow.md};
    }
`

const CardMedia = styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 4 / 3;
    background: radial-gradient(70% 70% at 50% 40%, rgba(255, 255, 255, 0.06), transparent 70%);
`

/**
 * Absolutely positioned inside the ratio box: a percentage height cannot
 * resolve against an `aspect-ratio` parent whose height is auto, which would
 * make every card fall back to its image's intrinsic ratio.
 */
const CardImage = styled(SmartImage)`
    position: absolute;
    inset: 0;
    padding: 18px;
    transition: transform 700ms ${({theme}) => theme.ease.out};

    ${Card}:hover & {
        transform: scale(1.05);
    }
`

const TypeChip = styled.span`
    position: absolute;
    top: 12px;
    left: 12px;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 5px 11px;
    border: 1px solid ${({theme}) => theme.line};
    border-radius: ${({theme}) => theme.radius.pill};
    background-color: rgba(8, 8, 10, 0.6);
    backdrop-filter: blur(6px);
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${({theme}) => theme.colorMuted};
`

const TypeChipIcon = styled(FontAwesomeIcon)`
    width: 11px;
    height: 11px;
    color: ${({theme}) => theme.accent};
`

const HotBadge = styled.span`
    position: absolute;
    top: 12px;
    right: 12px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 11px;
    border-radius: ${({theme}) => theme.radius.pill};
    background-color: ${({theme}) => theme.accent};
    color: ${({theme}) => theme.accentContrast};
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`

const HotBadgeIcon = styled(FontAwesomeIcon)`
    width: 11px;
    height: 11px;
`

const CardBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 16px 18px 0;
    /* Keeps the footers of a row aligned no matter how long the names are. */
    flex: 1;
`

const CardProducer = styled.span`
    ${eyebrow};
    font-size: 10px;
`

const CardName = styled.h3`
    font-size: 18px;
    line-height: 1.25;
    letter-spacing: -0.02em;
    color: ${({theme}) => theme.color};
`

const CardMeta = styled.div`
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-top: auto;
    padding-top: 10px;
`

const CardPrice = styled.span<{ $highlight?: boolean }>`
    font-size: 19px;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: ${({theme, $highlight}) => ($highlight ? theme.accent : theme.color)};
`

const CardPriceUnit = styled.span`
    font-size: 12.5px;
    font-weight: 500;
    color: ${({theme}) => theme.colorFaint};
`

/** Monochrome stock hint: a bright dot means plenty, a dim one means few. */
const Availability = styled.span<{ $low: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-left: auto;
    font-size: 12px;
    font-weight: 600;
    color: ${({theme, $low}) => ($low ? theme.colorFaint : theme.colorMuted)};

    &::before {
        content: '';
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background-color: ${({theme, $low}) => ($low ? theme.colorFaint : theme.color)};
    }
`

const CardFooter = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    /* Pinned to the bottom so every card in a row shares one action line. */
    margin-top: 16px;
    padding: 14px 18px;
    border-top: 1px solid ${({theme}) => theme.lineFaint};
`

/** Primary action slot — the button or the stepper, always the same width. */
const CardAction = styled.div`
    display: flex;
    flex: 1;
    min-width: 0;
`

/* ------------------------------------------------------- empty and skeletons */

const Empty = styled.div`
    ${container};
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    padding: clamp(56px, 10vw, 120px) 0;
    text-align: center;
`

const EmptyIcon = styled(FontAwesomeIcon)`
    width: 30px;
    height: 30px;
    color: ${({theme}) => theme.colorFaint};
`

const EmptyTitle = styled.h2`
    font-size: 22px;
    color: ${({theme}) => theme.color};
`

const EmptyText = styled.p`
    max-width: 42ch;
    font-size: 15px;
    color: ${({theme}) => theme.colorMuted};
`

const pulse = keyframes`
    0%, 100% {
        opacity: 0.5;
    }
    50% {
        opacity: 1;
    }
`

const SkeletonCard = styled.div`
    display: flex;
    flex-direction: column;
    height: 340px;
    border: 1px solid ${({theme}) => theme.lineFaint};
    border-radius: ${({theme}) => theme.radius.md};
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.01));
    animation: ${pulse} 1.6s ease-in-out infinite;
`

export const Styled = {
    Page,
    Head,
    HeadText,
    Eyebrow,
    Title,
    Lead,
    HeadCount,

    Toolbar,
    ToolbarInner,
    Tabs,
    Tab,
    TabCount,
    ToolbarRight,
    Search,
    SearchIcon,
    SearchInput,
    ClearSearch,
    SortHolder,
    Sort,
    SortIcon,
    SortChevron,

    Grid,

    Card,
    CardMedia,
    CardImage,
    TypeChip,
    TypeChipIcon,
    HotBadge,
    HotBadgeIcon,
    CardBody,
    CardProducer,
    CardName,
    CardMeta,
    CardPrice,
    CardPriceUnit,
    Availability,
    CardFooter,
    CardAction,

    Empty,
    EmptyIcon,
    EmptyTitle,
    EmptyText,
    SkeletonCard
}
